import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Button, { ButtonTypes } from 'components/common/button';
import MarkTaskClosedAndCreateCompletionItem from 'actions/conversation_item/task/mark_task_closed_and_create_completion_item';
import MarkTaskOpenedAndCreateReopenedItem from 'actions/conversation_item/task/mark_task_opened_and_create_reopend_item';
import SearchableTaskAssignmentMenu from 'components/lib/assignment_menu/searchable_task_assignment_menu';
import TaskStatusIndicator from './task_status_indicator';
import { useExecuteAction } from 'components/hooks/connect_hooks';

export default function TaskHeader({
  commentCount,
  isPending,
  location,
  onChangeAssignee,
  isLink,
  item,
  hasConversationGeneral,
}) {
  const executeAction = useExecuteAction();
  const { t } = useTranslation();

  const task = item.content;
  const isClosed = task.isClosed();
  const isReadOnly = isClosed || isLink || !hasConversationGeneral;
  const onToggleTask = useCallback(
    evt => {
      evt.stopPropagation();
      if (item.content.isClosed()) {
        executeAction(MarkTaskOpenedAndCreateReopenedItem, {
          conversationItem: item,
          location,
        });
      } else {
        executeAction(MarkTaskClosedAndCreateCompletionItem, {
          conversationItem: item,
          location,
        });
      }
    },
    [item, location]
  );

  function renderFinishButton() {
    if (isLink || !hasConversationGeneral) {
      return null;
    }

    return (
      <StyledButton
        buttonType={ButtonTypes.OUTLINE}
        className={classnames({ 'taskHeader-toggle-toReopen': isClosed, 'taskHeader-toggle-toClose': !isClosed })}
        data-aid="taskHeader-toggle"
        disabled={isPending}
        isSmall
        onClick={onToggleTask}
      >
        {isClosed ? t('Reopen') : t('Close')}
      </StyledButton>
    );
  }

  function renderAssignee() {
    let assignee = task.assignee;
    return (
      <StyledAssignmentMenu
        agentId={assignee && assignee.agentId}
        onSelect={onChangeAssignee}
        readOnly={isReadOnly}
        routingGroupId={assignee && assignee.routingGroupId}
      />
    );
  }
  return (
    <StyledTaskHeader data-aid="commentableTaskItem-header">
      <TaskStatusIndicator isComplete={isClosed} timestamp={task.dueAt} />{' '}
      <Trans
        components={{ status: <StyledStatus data-aid="taskHeader-status" />, word: <StyledWord /> }}
        i18nKey={isClosed ? '<status>Closed Task</status><word>for</word>' : '<status>Task</status><word>for</word>'}
      />
      {renderAssignee()} {renderFinishButton()}
    </StyledTaskHeader>
  );
}

TaskHeader.propTypes = {
  commentCount: PropTypes.number,
  hasConversationGeneral: PropTypes.bool,
  isPending: PropTypes.bool,
  location: PropTypes.string.isRequired,
  onChangeAssignee: PropTypes.func.isRequired,
  isLink: PropTypes.bool,
  item: PropTypes.object.isRequired,
};

const StyledStatus = styled.strong`
  white-space: nowrap;
`;

const StyledTaskHeader = styled.div`
  align-items: center;
  display: flex;
  height: 30px;
  margin-right: ${p => p.theme.spacing.medium};
`;

export const StyledButton = styled(Button)`
  margin-left: ${p => p.theme.spacing.medium};
`;

const StyledWord = styled.span`
  padding: 0 3px;
`;

export const StyledAssignmentMenu = styled(SearchableTaskAssignmentMenu)`
  &.searchableTaskAssignment {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    .searchableTaskAssignment-assignee-text,
    .searchableTaskAssignment-assignee-text-name {
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;
